import React, { useState, useEffect } from "react";
import { animateScroll } from "react-scroll";
import { Link } from "gatsby";
import {
  Call as CallIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import {
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
} from "@mui/material";

const Header = ({ phoneNumber }) => {
  const defaultPhoneNumber = "800-816-1003";

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null); // Separate state for mobile

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleDropdownOpen = (index) => {
    setOpenDropdownIndex(index);
  };

  const handleDropdownClose = () => {
    setOpenDropdownIndex(null);
  };

  const toggleMobileDropdown = (index) => {
    setMobileDropdownOpen(mobileDropdownOpen === index ? null : index);
  };

  const navLinks = [
    // {
    //   name: "Debt Solutions",
    //   dropdown: [
    //     { name: "Credit Card Debt Relief", path: "credit-card-debt-relief" },
    //     { name: "Debt Consolidation", path: "debt-consolidation" },
    //     { name: "Credit Counseling", path: "credit-counseling" },
    //     { name: "Debt Management", path: "debt-management" },
    //     {
    //       name: "Payday Loan Consolidation",
    //       path: "payday-loan-consolidation",
    //     },
    //   ],
    // },
    {
      name: "Apply",
      path: "/apply/",
    },
    {
      name: "Service Areas",
      path: "/service-areas/",
    },
    {
      name: "Contact",
      path: "/#contact-us",
    },
  ];

  useEffect(() => {
    animateScroll.scrollToTop({ duration: 400, smooth: true });
  }, []);

  return (
    <header className="relative inset-x-0 top-0 z-50 bg-white shadow-md">
      <nav className="py-4" aria-label="Global">
        <div className="container flex items-center justify-between">
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <IconButton onClick={toggleMobileMenu}>
              <MenuIcon />
            </IconButton>
          </div>

          {/* Logo */}
          <Link to="/">
            <img
              className="h-8 sm:h-10"
              src="/images/adc-white-logo.webp"
              alt="Logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map(({ name, path, dropdown }, index) =>
              dropdown ? (
                <div className="relative h-full" key={index}>
                  {/* Button and Dropdown Wrapper */}
                  <div
                    className="relative"
                    onMouseEnter={() => handleDropdownOpen(index)}
                    onMouseLeave={() => handleDropdownClose()}
                    role="presentation"
                  >
                    {/* Dropdown Button */}
                    <button className="transition text-green-600 font-semibold text-sm xl:text-xl flex items-center">
                      {name}
                      <KeyboardArrowUpIcon
                        className={`ml-1 text-green-600 transform transition-transform ${
                          openDropdownIndex === index
                            ? "rotate-0"
                            : "rotate-180"
                        }`}
                      />
                    </button>

                    {/* Dropdown Menu */}
                    {openDropdownIndex === index && (
                      <div className="absolute left-0 mt-4 w-64 bg-gray-100 border border-gray-200 rounded-lg shadow-lg z-50">
                        {/* Dropdown Arrow */}
                        <div className="absolute -top-2 left-6 w-4 h-4 bg-gray-100 border-l border-t border-gray-200 transform rotate-45"></div>

                        {/* Dropdown List */}
                        <ul className="py-2">
                          {dropdown.map((item, idx) => (
                            <li key={idx}>
                              <Link
                                to={`/debt-relief-in/${item.path}/`}
                                className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                              >
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Link
                  key={index}
                  to={path}
                  className="text-green-600 font-semibold text-sm xl:text-xl hover:text-green-800"
                >
                  {name}
                </Link>
              )
            )}
          </div>

          {/* Call Button */}
          <a
            className="hidden md:flex transition duration-300 text-white bg-green-800 text-md font-semibold hover:scale-[0.95] px-2 py-1 items-center rounded"
            href={`tel:${(phoneNumber || defaultPhoneNumber).replaceAll(
              "-",
              ""
            )}`}
          >
            {phoneNumber || defaultPhoneNumber}
          </a>
        </div>
      </nav>

      {/* Mobile Drawer Menu */}
      <Drawer anchor="left" open={mobileMenuOpen} onClose={toggleMobileMenu}>
        <div className="w-80 py-8">
          <div className="flex px-4 items-center justify-between mb-8">
            <Link to="/">
              <img
                src="/images/favicon.webp"
                className="w-16 h-auto"
                alt="Logo"
              />
            </Link>
            <button
              className="rounded-md p-2 hover:text-green-700 text-black"
              onClick={toggleMobileMenu}
              type="button"
            >
              <CloseIcon />
            </button>
          </div>

          <List className="px-2">
            {navLinks.map(({ name, path, dropdown }, index) => (
              <div key={index}>
                {dropdown ? (
                  <>
                    <ListItem
                      button
                      onClick={() => toggleMobileDropdown(index)}
                    >
                      <ListItemText primary={name} />
                      <KeyboardArrowUpIcon
                        className={`transform transition-transform ${
                          mobileDropdownOpen === index
                            ? "rotate-0"
                            : "rotate-180"
                        }`}
                      />
                    </ListItem>

                    <Collapse
                      in={mobileDropdownOpen === index}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding>
                        {dropdown.map((item, idx) => (
                          <ListItem
                            key={idx}
                            button
                            component={Link}
                            to={item.path}
                            className="pl-8"
                            onClick={toggleMobileMenu}
                          >
                            <ListItemText primary={item.name} />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </>
                ) : (
                  <ListItem
                    button
                    component={Link}
                    to={path}
                    onClick={toggleMobileMenu}
                  >
                    <ListItemText primary={name} />
                  </ListItem>
                )}
              </div>
            ))}
            <ListItem
              button
              component="a"
              href={`tel:${phoneNumber || defaultPhoneNumber}`}
            >
              <CallIcon className="me-2" />
              <ListItemText primary="Free Consultation" />
            </ListItem>
          </List>
        </div>
      </Drawer>
    </header>
  );
};

export default Header;
